import {
  ContextListPropertyNode,
  ContextPropertyNode,
  ContextProviderNode,
} from '@decisions/server/DecisionsFramework/Design/Properties';
import { EditorNames } from 'components/Editors/EditorNames';
import { initializeNumericValue } from 'utils/NumericFormattingUtils';
import { getUserCulture } from 'utils/UserCulture';
import { isNullableType } from 'components/Editors/utils/InputTypeHelpers';
import {
  IPropertyChild,
  childValueIsNull,
  getChildValue,
  getChildValueAsArray,
} from 'components/property-editors/utils/ProviderNodeUtils';
import { getMomentForEditor } from 'components/Editors/utils/EditorDateTimeUtils';
import { getDateFormat, getMomentAndDateString } from 'components/inputs/date-time/helpers/DateTimePickerHelpers';
import { InputMappingConstants } from 'components/property-editors/MappingConstants';
import { getTimeSpanDisplayValue, getValue } from 'utils/TimeSpanUtils';
import { FileDataWithReferencePath } from '@decisions/server/DecisionsFramework/Data/DataTypes/FileDataWithReferencePath';
import { GetSize } from 'utils/StringUtils';
import { ImageInfo } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Image';
import { getEntityByID } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder/FolderService';
import { getDecisionsTypeById, getTypeDisplayName } from 'utils/DecisionsTypeUtils';
import { DataPair } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ContextData';
import { shortenString } from 'utils/StringUtils';
import { getWrappedTypeName } from 'utils/TypeUtils';
import { getFormattedDateString, isMinDate, isIsoDateStringQuickAndDirty } from 'utils/DateTimeUtils';
import { DynamicObject, isDynamicObject } from '@decisions/server/DecisionsFramework/Design/Flow/Mapping/DynamicObject';
import { DesignerThicknessFacade } from '@decisions/server/DecisionsFramework/Design/Properties/Attributes/DesignerThicknessFacade';
import { getColorEditorString } from 'utils/ColorUtils';

export interface ProviderNodeStringValueGetter {
  (node: ContextProviderNode, currentMapping?: string): string | Promise<string>;
}

export async function getEditorValueString(node: ContextProviderNode, currentMapping?: string) {
  const getter: ProviderNodeStringValueGetter | undefined =
    EditorNameToGetterMap[(node.Node as ContextPropertyNode).MvcEditorName];

  if (!getter) {
    // TODO some basic fallback using typeof
    return '';
  }

  return getter(node, currentMapping);
}

/**
 * @param nodes to get values string
 * @returns one level deep, comma separated value string.
 */
export async function getValueStringForEditors(nodes: ContextProviderNode[]) {
  if (!nodes) {
    return '';
  }
  const strings = await Promise.all(nodes.map(node => getEditorValueString(node)));

  return strings.filter(str => !!str).join(', ');
}

export async function getValueStringMapForEditors(nodes: ContextProviderNode[]): Promise<Map<string, string>> {
  const kvp = new Map<string, string>();
  if (!nodes) {
    return kvp;
  }
  await Promise.all(
    nodes.map(async node => {
      const val = await getEditorValueString(node);
      return kvp.set((node.Node as ContextPropertyNode).DisplayLabel || node.Node.Name, val);
    })
  );

  return kvp;
}

export function getSimpleStringValue(node: ContextProviderNode) {
  return node.Value?.OutputValue !== null && node.Value?.OutputValue !== undefined ? node.Value.OutputValue + '' : '';
}

export function getDigitsEditorValue(node: ContextProviderNode) {
  const decimalSeparator = getUserCulture().NumberDecimalSeparator;
  const value = initializeNumericValue(node.Value.OutputValue, isNullableType(node as IPropertyChild));
  return !childValueIsNull(node as IPropertyChild) ? value.toString().replace('.', decimalSeparator) : '';
}

export function getDateTimeValue(node: ContextProviderNode, currentMapping?: string) {
  const childValue = getChildValue(node);
  const isConstantDateTime = currentMapping === InputMappingConstants.MAPPING_DATETIME;
  const date = getMomentForEditor(childValue, !isConstantDateTime) ?? undefined;
  const { parsedDateString } = getMomentAndDateString(getDateFormat(isConstantDateTime), date);
  return parsedDateString ?? '';
}

export function getTimeSpanValue(node: ContextProviderNode) {
  const childValue = getChildValue(node);
  const time = getValue(childValue);
  return getTimeSpanDisplayValue(time);
}

export function getStringArrayValue(node: ContextProviderNode) {
  return getChildValueAsArray(node).join(', ');
}

export function getDateTimeArrayValue(node: ContextProviderNode) {
  //Todo Culture?. Currently not working in earlier versions as well
  return (node.Node as ContextListPropertyNode).ItemLabels?.join(', ');
}

export function getFilePickerStringValue(node: ContextProviderNode) {
  const fileData: FileDataWithReferencePath = getChildValue(node);
  return fileData && `${fileData?.FileName}: ${GetSize(fileData.ContentLength)}`;
}

export function getMultiFilePickerStringValue(node: ContextProviderNode) {
  const filesData: FileDataWithReferencePath[] = getChildValue(node);
  const fileNamesWithSize = filesData?.map(file => {
    return `${file.FileName}: ${GetSize(file.ContentLength)}`;
  });
  return fileNamesWithSize?.length ? fileNamesWithSize.join(', ') : '';
}

export function getMergeStringEditorValue(node: ContextProviderNode) {
  return getMergedTextDisplayValue(getChildValue(node));
}

export function geGridPopupEditorStringValue(node: ContextProviderNode) {
  const childValue = getChildValue(node);
  return childValue && typeof childValue === 'string' ? `Value: ${childValue}` : '';
}

export function getLinkArrayEditorStringValue(node: ContextProviderNode) {
  return (node.Node as ContextListPropertyNode).ItemLabels?.join(', ');
}

export function getFileContentPickerStringValue(node: ContextProviderNode) {
  const bytes: number[] | null = getChildValue(node);
  const length = bytes?.length || 0;
  return bytes ? `Content: ${GetSize(length)}` : '';
}

export function getPasswordEditorStringValue(node: ContextProviderNode) {
  //returning empty because don't want to show password in plain text
  return '';
}

export function getColorEditorStringValue(node: ContextProviderNode) {
  return getColorEditorString(node);
}

export function getImageInfoEditorStringValue(node: ContextProviderNode) {
  const childValue: ImageInfo = getChildValue(node);
  return childValue && childValue.ImageName;
}

export async function getEntityPickerEditorStringValue(node: ContextProviderNode) {
  const childValue = getChildValue(node);
  let entityName = '';
  if (childValue) {
    const entityHeader = await getEntityByID({ entityID: childValue });
    entityName = entityHeader.EntityName;
  }
  return entityName;
}

export async function getTypePickerEditorStringValue(node: ContextProviderNode) {
  const childValue = getChildValue(node);
  let typeName = '';
  if (childValue) {
    const type = await getDecisionsTypeById(childValue);
    typeName = await getTypeDisplayName(type);
  }
  return typeName;
}

export function getPopupEditorValueString(node: ContextProviderNode) {
  // for most things, the value will be stringified already:

  const value = node.Value?.OutputValue;
  if (typeof value === 'string') {
    return value;
  }

  if (!value) {
    return '';
  }

  if (isDynamicObject(value)) {
    // there is some special stuff with DynamicObjects - the value won't be a string, it'll be DataPairs:
    const addedKeys: string[] = [];
    const data: DataPair[] = value.Data;
    if (!data) {
      return (value as DynamicObject).Type.Name;
    }
    return data
      .map(pair => {
        const { __type, Name, OutputValue } = pair;
        if (OutputValue === null || OutputValue === undefined || addedKeys.includes(Name)) {
          // skip nulls, but not all falsy values, as 0 and false are still worth-while
          // also because of... what? Inheritance? these seem to be able to have duplicate entries for a given prop name.
          return '';
        }

        if (typeof OutputValue === 'object') {
          let typeString = getWrappedTypeName(__type);
          typeString = `${Name}:${typeString}`;
          typeString = shortenString(typeString, 30); // keep 30 chars inclusive of ellipsis
          typeString = typeString + (Array.isArray(OutputValue) ? '[]' : '');
          addedKeys.push(Name);
          return typeString;
        }
        let value = OutputValue;
        if (typeof value === 'string' && isIsoDateStringQuickAndDirty(value)) {
          // seems to be a date value:
          value = isMinDate(value) ? '' : getFormattedDateString(value);
        }

        addedKeys.push(Name);
        return !!(value + '') && `${Name}:${value}`;
      })
      .filter(value => value)
      .join(' | ');
  }
  return '';
}

export function getThicknessEditorStringValue(node: ContextProviderNode) {
  const value: DesignerThicknessFacade = getChildValue(node);
  if (!value) {
    return '';
  }
  return value.UseUniformValue
    ? `Left:${value.UniformValue},Top:${value.UniformValue},Right:${value.UniformValue},Bottom:${value.UniformValue}`
    : `Left:${value.Left},Top:${value.Top},Right:${value.Right},Bottom:${value.Bottom}`;
}

function getMergedTextLength(value: string) {
  return value.replace(/(<.+?>|&nbsp;)/g, '').trim().length;
}

export function getMergedTextDisplayValue(value: string) {
  return `Length: ${getMergedTextLength(value)}`;
}

const EditorNameToGetterMap: {
  [key: string]: ProviderNodeStringValueGetter;
} = {
  [EditorNames.String]: getSimpleStringValue,
  [EditorNames.StringEditor]: getSimpleStringValue,
  [EditorNames.Digits]: getDigitsEditorValue, // TODO need to consider number formats for this?
  [EditorNames.Combo]: getSimpleStringValue,
  [EditorNames.CheckBox]: getSimpleStringValue,
  [EditorNames.SuggestStringEditor]: getSimpleStringValue,
  [EditorNames.EditableDropDown]: getSimpleStringValue, //Same as combo
  [EditorNames.DataDescriptionPathEditor]: getSimpleStringValue,
  [EditorNames.TextArea]: getSimpleStringValue,
  [EditorNames.DefinedTypeEditor]: getSimpleStringValue,
  [EditorNames.GroupsPicker]: getSimpleStringValue,
  [EditorNames.AccountsPicker]: getSimpleStringValue,
  [EditorNames.SelectByIcon]: getSimpleStringValue,
  [EditorNames.EntityPicker]: getEntityPickerEditorStringValue,
  [EditorNames.FolderPickerEditor]: getEntityPickerEditorStringValue,
  [EditorNames.SelectElementRegistrationEditor]: getEntityPickerEditorStringValue,
  [EditorNames.TypePickerEditor]: getTypePickerEditorStringValue,
  [EditorNames.PasswordEditor]: getPasswordEditorStringValue,
  [EditorNames.DatePicker]: getDateTimeValue,
  [EditorNames.DateAndTimePicker]: getDateTimeValue,
  [EditorNames.TimeSpan]: getTimeSpanValue,
  [EditorNames.StringArray]: getStringArrayValue,
  [EditorNames.CharArrayEditor]: getStringArrayValue,
  [EditorNames.NumericArray]: getStringArrayValue,
  [EditorNames.DateTimeArray]: getDateTimeArrayValue,
  [EditorNames.FilePickerEditor]: getFilePickerStringValue,
  [EditorNames.MultiFilePickerEditor]: getMultiFilePickerStringValue,
  [EditorNames.FileContentPicker]: getFileContentPickerStringValue,
  [EditorNames.MergeTextEditor]: getMergeStringEditorValue,
  [EditorNames.GridPopup]: getPopupEditorValueString,
  [EditorNames.LinkArrayEditor]: getLinkArrayEditorStringValue,
  [EditorNames.ColorEditor]: getColorEditorStringValue,
  [EditorNames.ImageInfo]: getImageInfoEditorStringValue,
  [EditorNames.Thickness]: getThicknessEditorStringValue,
  [EditorNames.RichText]: getSimpleStringValue,

  [EditorNames.TimePickerEditor]: getSimpleStringValue,
  [EditorNames.CalendarEditor]: getSimpleStringValue,
  [EditorNames.CheckList]: getSimpleStringValue,
  [EditorNames.RadioList]: getSimpleStringValue,
  [EditorNames.TokenPicker]: getSimpleStringValue,
  [EditorNames.StringFormat]: getSimpleStringValue,
  [EditorNames.SelectTagsEditor]: getSimpleStringValue,
  [EditorNames.NullableCheckBox]: getSimpleStringValue,
  [EditorNames.Readonly]: getSimpleStringValue,
  [EditorNames.DisplayString]: getSimpleStringValue,
  [EditorNames.StringWithPrefix]: getSimpleStringValue,
};
